import { ModernButton } from "./modern-button";
import Phaser from "phaser";
import { hideJoysticks, showJoysticks } from "../controls/controls-touch";
import { COLORS, hexToPhaser } from "./colors";
import { TILE_SIZE } from "../models/common";

interface TutorialText {
  text: string[];
  arrow?: {
    tileX?: number;
    tileY?: number;
    screenX?: number;
    screenY?: number;
  };
  discordUrl?: string;
}

export class TutorialPopup {
  private activeTutorialName: string | null = null;
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private visible: boolean = false;
  private currentTextIndex: number = 0;
  private tutorialData: TutorialText = { text: [] };
  private textObj: Phaser.GameObjects.Text | null = null;
  private arrowSprite: Phaser.GameObjects.Image | null = null;
  private windowManager: any;
  private uiLayer: Phaser.GameObjects.Container;
  private clickZone: Phaser.GameObjects.Zone | null = null;
  private onComplete: (() => void) | null = null;
  private updateEvent: Phaser.Time.TimerEvent | null = null;

  constructor(
    scene: Phaser.Scene,
    windowManager: any,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.windowManager = windowManager;
    this.uiLayer = uiLayer;
  }

  static preload(scene: Phaser.Scene) {
    scene.load.image("arrow", "/sprites/arrow.png");
  }

  /**
   * Show a tutorial popup with the given tutorial data
   */
  show(
    tutorialName: string,
    tutorialData: TutorialText,
    onComplete?: () => void,
  ) {
    if (this.visible) {
      return;
    }

    this.activeTutorialName = tutorialName;
    this.tutorialData = tutorialData;
    this.currentTextIndex = 0;
    this.onComplete = onComplete || null;

    // Close all windows
    this.windowManager.closeAllWindows();

    // Hide joysticks and action bar
    hideJoysticks();
    this.windowManager.hideActionSlots();

    // Create the popup UI
    this.createPopup();

    // Start arrow update loop if tutorial has an arrow
    if (this.tutorialData && this.tutorialData.arrow) {
      this.startArrowUpdate();
    }

    this.visible = true;
  }

  private createPopup() {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Positioning constants
    const marginTop = 120; // Distance from top of screen (enough to not hide UI buttons)
    const marginSides = 30; // Distance from left and right edges
    const boxWidth = Math.max((gameWidth * 2) / 3 - marginSides * 2, 280); // Width with side margins
    const maxBoxHeight = gameHeight * 0.4; // Maximum height (40% of screen)
    const padding = 20; // Padding inside box
    const cornerRadius = 12;

    // Create arrow first if tutorial has one (so it's added to uiLayer before container)
    if (this.tutorialData && this.tutorialData.arrow) {
      this.createArrow();
    }

    // Create main container at top center
    this.container = this.scene.add.container(gameWidth / 2, marginTop);
    this.container.setScrollFactor(0);
    this.container.setDepth(15001);
    this.uiLayer.add(this.container);

    // Create background graphics first (so it renders behind text)
    const bg = this.scene.add.graphics();
    bg.setDepth(1); // Render in front of arrow but behind text
    this.container.add(bg);

    // Text display - create first to measure height
    this.textObj = this.scene.add.text(0, 0, "", {
      fontSize: "24px",
      fontFamily: "Anton",
      color: "#ffffff",
      align: "center",
      wordWrap: { width: boxWidth - padding * 2 },
    });
    this.textObj.setShadow(2, 2, "#000000", 0.7);
    this.textObj.setOrigin(0.5, 0);

    // "Tap to continue" indicator text at bottom (with end-screen styling)
    const continueText = this.scene.add.text(
      gameWidth / 2,
      gameHeight - 120,
      "TAP TO CONTINUE",
      {
        fontSize: "32px",
        fontFamily: "Anton",
        color: "#ffffff",
        align: "center",
        shadow: {
          offsetX: 0,
          offsetY: 6,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      },
    );
    continueText.setOrigin(0.5, 0.5);
    continueText.setScrollFactor(0);
    continueText.setDepth(15002);

    // Make text blink
    this.scene.tweens.add({
      targets: continueText,
      alpha: {
        from: 0.3,
        to: 1,
      },
      duration: 800,
      repeat: -1,
      yoyo: true,
    });

    this.uiLayer.add(continueText);

    // Function to update box size based on content
    const updateBoxSize = () => {
      if (!this.textObj) return;

      const textHeight = this.textObj.displayHeight;
      const totalContentHeight = textHeight + padding * 2;
      const boxHeight = Math.min(totalContentHeight, maxBoxHeight);

      // Position text in center of box
      const textY = -boxHeight / 2 + padding;

      this.textObj!.setPosition(0, textY);

      // Update background
      bg.clear();
      bg.fillStyle(hexToPhaser(COLORS.BG), 0.95);
      bg.fillRoundedRect(
        -boxWidth / 2,
        -boxHeight / 2,
        boxWidth,
        boxHeight,
        cornerRadius,
      );

      // Border
      bg.lineStyle(3, hexToPhaser(COLORS.BLACK), 0.8);
      bg.strokeRoundedRect(
        -boxWidth / 2,
        -boxHeight / 2,
        boxWidth,
        boxHeight,
        cornerRadius,
      );
    };

    this.container.add(this.textObj);

    // Create invisible clickable zone for the entire screen
    this.clickZone = this.scene.add.zone(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
    );
    this.clickZone.setScrollFactor(0);
    this.clickZone.setInteractive();
    this.clickZone.setDepth(15001);
    this.clickZone.on("pointerdown", () => this.nextText());
    this.uiLayer.add(this.clickZone);

    // Store updateBoxSize and continueText for later use
    (this as any).updateBoxSize = updateBoxSize;
    (this as any).continueText = continueText;

    // Create Discord button if this tutorial has a discordUrl (shown on last page)
    if (this.tutorialData.discordUrl) {
      this.createDiscordButton(gameWidth, gameHeight);
    }

    // Display first text
    this.displayCurrentText();
  }

  private displayCurrentText() {
    if (
      !this.textObj ||
      !this.tutorialData ||
      this.currentTextIndex >= this.tutorialData.text.length
    ) {
      return;
    }

    const text = this.tutorialData.text[this.currentTextIndex];
    this.textObj.setText(text);

    // Update box size after text changes
    const updateBoxSize = (this as any).updateBoxSize;
    if (updateBoxSize) {
      updateBoxSize();
    }

    // Update arrow position if it exists
    if (this.arrowSprite && this.tutorialData.arrow) {
      this.updateArrowPosition();
    }
  }

  private createDiscordButton(gameWidth: number, gameHeight: number) {
    const url = this.tutorialData.discordUrl!;
    const btnY = gameHeight - 220;
    const btnX = gameWidth / 2;

    const btn = new ModernButton({
      scene: this.scene,
      x: btnX,
      y: btnY,
      text: "Join Discord",
      style: "discord",
      onClick: () => {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isAndroid = /Android/i.test(navigator.userAgent);
        const inviteCode = url.replace(/^https?:\/\/(?:www\.)?discord\.gg\//, "");
        if (isIOS) {
          // Listen for visibility change — if the page hides, Discord app opened.
          // If it stays visible after the timeout, Discord isn't installed so open
          // the link in Safari (external browser).
          let appOpened = false;
          const onVisibilityChange = () => {
            if (document.hidden) {
              appOpened = true;
            }
            document.removeEventListener("visibilitychange", onVisibilityChange);
          };
          document.addEventListener("visibilitychange", onVisibilityChange);
          window.open(`discord://discord.gg/${inviteCode}`, "_blank");
          setTimeout(() => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (!appOpened) {
              // Discord app not installed — open in external Safari
              window.open(url, "_blank");
            }
          }, 1000);
        } else if (isAndroid) {
          // Android intent: opens Discord app if installed; browser_fallback_url
          // opens the URL in an external browser if Discord is not installed.
          window.open(`intent://discord.gg/${inviteCode}#Intent;scheme=https;package=com.discord;end;S.browser_fallback_url=${encodeURIComponent(url)}`, "_blank");
        } else {
          window.open(url, "_blank");
        }
        // Dismiss the tutorial and mark it as seen when Discord is joined
        this.nextText();
      },
    });
    btn.setScrollFactor(0);
    btn.setDepth(15003);
    this.uiLayer.add(btn);

    (this as any).discordBtn = btn;
    (this as any).discordLabel = null;
  }

  private createArrow() {
    if (!this.tutorialData.arrow) {
      return;
    }

    this.arrowSprite = this.scene.add.image(0, 0, "arrow");

    // Always use screen space (scrollFactor 0) for consistency
    // We'll handle world-to-screen conversion in updateArrowPosition
    this.arrowSprite.setScrollFactor(0);
    this.arrowSprite.setDepth(15000);
    // Add to uiLayer before container so it renders behind
    this.uiLayer.add(this.arrowSprite);

    this.arrowSprite.setScale(2);
    this.arrowSprite.setOrigin(0.5, 0); // Anchor at top-center to avoid overlapping target tile

    this.updateArrowPosition();
  }

  private startArrowUpdate() {
    // Start update loop to continuously update arrow rotation as player moves
    this.scene.events.on("update", this.updateArrowPosition, this);
  }

  private stopArrowUpdate() {
    // Stop the update event
    this.scene.events.off("update", this.updateArrowPosition, this);
  }

  private updateArrowPosition() {
    if (!this.arrowSprite || !this.tutorialData.arrow) {
      return;
    }

    const arrow = this.tutorialData.arrow;
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const camera = this.scene.cameras.main;

    let screenArrowX: number; // Position in screen space where the arrow will be displayed
    let screenArrowY: number;
    let targetScreenX: number = 0; // Target position in screen space
    let targetScreenY: number = 0;
    let rotation: number = 0;

    if (arrow.screenX !== undefined && arrow.screenY !== undefined) {
      // Screen coordinates (for UI elements) - already in screen space
      targetScreenX = arrow.screenX * gameWidth;
      targetScreenY = arrow.screenY * gameHeight;

      // For screen-based arrows, position them at the target (not clamped)
      screenArrowX = targetScreenX;
      screenArrowY = targetScreenY;
    } else if (arrow.tileX !== undefined && arrow.tileY !== undefined) {
      // Tile coordinates - convert world to screen space
      const worldTargetX = arrow.tileX * TILE_SIZE + TILE_SIZE / 2;
      const worldTargetY = arrow.tileY * TILE_SIZE + TILE_SIZE / 2;

      // Convert world coordinates to screen space
      if (camera) {
        targetScreenX = worldTargetX - camera.scrollX;
        targetScreenY = worldTargetY - camera.scrollY;
      }

      // Check if target is within camera bounds
      const isWithinBounds =
        targetScreenX >= 0 &&
        targetScreenX <= gameWidth &&
        targetScreenY >= 0 &&
        targetScreenY <= gameHeight;

      if (isWithinBounds) {
        // Target is visible - position arrow directly at target
        screenArrowX = targetScreenX;
        screenArrowY = targetScreenY;
      } else {
        // Target is outside screen bounds - clamp arrow to edge of screen
        const edgePadding = 30; // Pixels from edge

        // Calculate direction from screen center to target
        const screenCenterX = gameWidth / 2;
        const screenCenterY = gameHeight / 2;
        const dx = targetScreenX - screenCenterX;
        const dy = targetScreenY - screenCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > 0) {
          // Normalize direction
          const normalizedX = dx / distance;
          const normalizedY = dy / distance;

          // Calculate bounds for clamped arrow
          const minX = edgePadding;
          const maxX = gameWidth - edgePadding;
          const minY = edgePadding;
          const maxY = gameHeight - edgePadding;

          // Find intersection with screen edges
          const halfWidth = (maxX - minX) / 2;
          const halfHeight = (maxY - minY) / 2;

          let clampX: number;
          let clampY: number;

          if (
            Math.abs(normalizedX) > 0 &&
            Math.abs(normalizedY / normalizedX) < halfHeight / halfWidth
          ) {
            // Clamp to left or right edge
            clampX = normalizedX > 0 ? maxX : minX;
            clampY =
              screenCenterY + (normalizedY * halfWidth) / Math.abs(normalizedX);
          } else if (Math.abs(normalizedY) > 0) {
            // Clamp to top or bottom edge
            clampY = normalizedY > 0 ? maxY : minY;
            clampX =
              screenCenterX +
              (normalizedX * halfHeight) / Math.abs(normalizedY);
          } else {
            clampX = screenCenterX;
            clampY = screenCenterY;
          }

          // Ensure we stay within bounds
          screenArrowX = Math.max(minX, Math.min(maxX, clampX));
          screenArrowY = Math.max(minY, Math.min(maxY, clampY));
        } else {
          screenArrowX = gameWidth / 2;
          screenArrowY = gameHeight / 2;
        }
      }
    } else {
      console.warn("[Arrow] No tile or screen coordinates defined");
      return;
    }

    // Calculate rotation based on direction from arrow position to target position
    // This ensures the arrow always points toward the target, whether on-screen or off-screen
    const dirX = targetScreenX - screenArrowX;
    const dirY = targetScreenY - screenArrowY;
    const distance = Math.sqrt(dirX * dirX + dirY * dirY);

    // Only calculate rotation if there's a distance (arrow position differs from target)
    if (distance > 0.1) {
      // Calculate angle using atan2
      // atan2(y, x) gives 0 radians pointing right, PI/2 pointing down
      // Since the sprite points up by default, we add PI/2 to rotate it correctly
      rotation = Math.atan2(dirY, dirX) + Math.PI / 2;
    } else {
      // If arrow is at target position, calculate rotation from player to target
      const player = (this.scene as any).player;
      if (player && arrow.tileX !== undefined && arrow.tileY !== undefined) {
        const TILE_SIZE = 32;
        const worldTargetX = arrow.tileX * TILE_SIZE + TILE_SIZE / 2;
        const worldTargetY = arrow.tileY * TILE_SIZE + TILE_SIZE / 2;
        const playerToTargetX = worldTargetX - player.x;
        const playerToTargetY = worldTargetY - player.y;
        rotation = Math.atan2(playerToTargetY, playerToTargetX) + Math.PI / 2;
      }
    }

    // Update position and rotation
    this.arrowSprite.setPosition(screenArrowX, screenArrowY);
    this.arrowSprite.setRotation(rotation);
    this.arrowSprite.setVisible(true);
  }

  private nextText() {
    this.currentTextIndex++;

    if (this.currentTextIndex >= this.tutorialData.text.length) {
      this.hide();
      if (this.onComplete) {
        this.onComplete();
      }
    } else {
      this.displayCurrentText();
    }
  }

  hide(hideArrow: boolean = false) {
    // Show joysticks and action bar again
    showJoysticks();
    this.windowManager.showActionSlots();

    // Clean up popup container (but keep arrow)
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }

    // Remove continue text
    const continueText = (this as any).continueText;
    if (continueText) {
      continueText.destroy();
      (this as any).continueText = null;
    }

    // Remove click zone
    if (this.clickZone) {
      this.clickZone.destroy();
      this.clickZone = null;
    }

    // Remove discord button if present
    const discordBtn = (this as any).discordBtn;
    if (discordBtn) {
      discordBtn.destroy();
      (this as any).discordBtn = null;
    }

    if (hideArrow) {
      // Hide the arrow
      if (this.arrowSprite) {
        this.arrowSprite.setVisible(false);
      }

      // Stop the arrow update loop
      this.stopArrowUpdate();
    }

    this.visible = false;
  }

  destroy() {
    // Complete cleanup including arrow
    this.hide(true);

    // Stop the update event
    if (this.updateEvent) {
      this.updateEvent.destroy();
      this.updateEvent = null;
    }

    if (this.arrowSprite) {
      this.arrowSprite.destroy();
      this.arrowSprite = null;
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  getActiveTutorial(): string | null {
    return this.activeTutorialName;
  }
}
